import { STATUS_CONFIG, FPS_CONFIG } from '../utils/Constants.js';

/**
 * Manages all UI elements and interactions
 */
export class UIController {
    constructor() {
        // Screen states
        this.viewerControls = null;
        this.statusBar = null;
        this.loadingOverlay = null;
        
        // Input elements
        this.fileInput = null;
        this.tripodMaskInput = null;
        this.customImageInput = null;
        
        // Control elements
        this.imageList = null;
        this.transitionDurationSlider = null;
        this.durationValue = null;
        this.initialRotationSlider = null;
        this.rotationValue = null;
        this.tripodMaskSizeSlider = null;
        this.tripodSizeValue = null;
        this.zoomIndicator = null;
        
        // Status elements
        this.statusText = null;
        this.loadingText = null;
        this.fpsText = null;
        
        // Modal elements
        this.customImageModal = null;
        this.customImagePreview = null;
        this.previewImg = null;
        this.connectionModal = null;
        this.connectionImageSelect = null;
        this.connectionStatus = null;
        
        // FPS tracking
        this.fps = 0;
        this.frameCount = 0;
        this.lastTime = performance.now();
        
        // Current values
        this.currentInitialRotation = null;
    }

    /**
     * Initialize all DOM elements
     */
    initializeDOMElements() {
        // Screens
        this.viewerControls = document.getElementById('viewer-controls');
        this.statusBar = document.getElementById('status-bar');
        this.loadingOverlay = document.getElementById('loading-overlay');
        
        // Inputs
        this.fileInput = document.getElementById('file-input');
        this.tripodMaskInput = document.getElementById('tripod-mask-input');
        this.customImageInput = document.getElementById('custom-image-input');
        
        // Controls
        this.imageList = document.getElementById('image-list');
        this.transitionDurationSlider = document.getElementById('transition-duration');
        this.durationValue = document.getElementById('duration-value');
        this.initialRotationSlider = document.getElementById('initial-rotation');
        this.rotationValue = document.getElementById('rotation-value');
        this.tripodMaskSizeSlider = document.getElementById('tripod-mask-size');
        this.tripodSizeValue = document.getElementById('tripod-size-value');
        this.zoomIndicator = document.getElementById('zoom-indicator');
        
        // Status
        this.statusText = document.getElementById('status-text');
        this.loadingText = document.getElementById('loading-text');
        this.fpsText = document.getElementById('fps-text');
        
        // Modals
        this.customImageModal = document.getElementById('custom-image-modal');
        this.customImagePreview = document.getElementById('custom-image-preview');
        this.previewImg = document.getElementById('preview-img');
        this.connectionModal = document.getElementById('connection-modal');
        this.connectionImageSelect = document.getElementById('connection-image-select');
        this.connectionStatus = document.getElementById('connection-status');
        
        console.log('✅ UI elements initialized');
    }

    /**
     * Setup event listeners for UI controls
     */
    setupEventListeners(callbacks) {
        // Main upload buttons
        const addNewImageBtn = document.getElementById('add-new-image');
        addNewImageBtn?.addEventListener('click', () => this.fileInput?.click());
        
        // Tripod mask button
        const loadTripodMaskBtn = document.getElementById('load-tripod-mask');
        loadTripodMaskBtn?.addEventListener('click', () => this.tripodMaskInput?.click());
        
        // Custom image button
        const addCustomImageBtn = document.getElementById('add-custom-image');
        addCustomImageBtn?.addEventListener('click', () => callbacks.onOpenCustomImageModal?.());
        
        // Connection button
        const createConnectionBtn = document.getElementById('create-connection');
        createConnectionBtn?.addEventListener('click', () => callbacks.onOpenConnectionModal?.());
        
        // File inputs
        this.fileInput?.addEventListener('change', (e) => callbacks.onImageSelected?.(e));
        this.tripodMaskInput?.addEventListener('change', (e) => callbacks.onTripodMaskSelected?.(e));
        this.customImageInput?.addEventListener('change', (e) => callbacks.onCustomImageSelected?.(e));
        
        // Sliders
        this.transitionDurationSlider?.addEventListener('input', (e) => {
            const value = parseInt(e.target.value);
            this.durationValue.textContent = `${value}ms`;
            callbacks.onTransitionDurationChanged?.(value);
        });
        
        this.initialRotationSlider?.addEventListener('input', (e) => {
            const value = parseInt(e.target.value);
            this.currentInitialRotation = value;
            this.rotationValue.textContent = `${value}°`;
        });
        
        this.tripodMaskSizeSlider?.addEventListener('input', (e) => {
            const value = parseFloat(e.target.value);
            this.tripodSizeValue.textContent = `${value.toFixed(1)}x`;
            callbacks.onTripodMaskSizeChanged?.(value);
        });
        
        // Custom image modal
        const closeCustomModal = document.getElementById('close-custom-modal');
        const cancelCustomImage = document.getElementById('cancel-custom-image');
        const selectCustomImage = document.getElementById('select-custom-image');
        const placeCustomImage = document.getElementById('place-custom-image');
        
        closeCustomModal?.addEventListener('click', () => this.closeCustomImageModal());
        cancelCustomImage?.addEventListener('click', () => this.closeCustomImageModal());
        selectCustomImage?.addEventListener('click', () => this.customImageInput?.click());
        placeCustomImage?.addEventListener('click', () => callbacks.onPlaceCustomImage?.());
        
        // Custom image size selection
        this.setupSizeSelection(callbacks);
        
        // Connection modal
        const closeConnectionModal = document.getElementById('close-connection-modal');
        const placeConnectionBtn = document.getElementById('place-connection');
        const cancelConnectionBtn = document.getElementById('cancel-connection');
        
        closeConnectionModal?.addEventListener('click', () => this.closeConnectionModal());
        placeConnectionBtn?.addEventListener('click', () => callbacks.onPlaceConnection?.());
        cancelConnectionBtn?.addEventListener('click', () => callbacks.onCancelConnection?.());
        
        this.connectionImageSelect?.addEventListener('change', (e) => {
            callbacks.onConnectionImageSelected?.(parseInt(e.target.value));
        });
        
        // Accordion
        this.setupAccordion();
        
        console.log('✅ Event listeners setup complete');
    }

    /**
     * Setup size selection buttons
     */
    setupSizeSelection(callbacks) {
        const sizeButtons = document.querySelectorAll('.custom-size-btn');
        sizeButtons.forEach(btn => {
            btn.addEventListener('click', () => {
                sizeButtons.forEach(b => {
                    b.classList.remove('bg-blue-600', 'text-white');
                    b.classList.add('bg-slate-700', 'hover:bg-slate-600');
                });
                
                btn.classList.remove('bg-slate-700', 'hover:bg-slate-600');
                btn.classList.add('bg-blue-600', 'text-white');
                
                const size = btn.getAttribute('data-size');
                callbacks.onCustomImageSizeSelected?.(size);
            });
        });
    }

    /**
     * Setup accordion functionality
     */
    setupAccordion() {
        const accordionHeaders = document.querySelectorAll('.accordion-header');
        
        accordionHeaders.forEach(header => {
            header.addEventListener('click', () => {
                const targetId = header.getAttribute('data-target');
                const content = document.getElementById(targetId);
                const arrow = header.querySelector('svg');
                
                if (content.classList.contains('hidden')) {
                    content.classList.remove('hidden');
                    arrow.style.transform = 'rotate(0deg)';
                } else {
                    content.classList.add('hidden');
                    arrow.style.transform = 'rotate(180deg)';
                }
            });
        });
    }

    /**
     * Show the viewer interface
     */
    showViewer() {
        this.viewerControls?.classList.remove('hidden');
        this.statusBar?.classList.remove('hidden');
        document.getElementById('fps-counter')?.classList.remove('hidden');
    }

    /**
     * Show loading overlay
     * @param {string} message - Loading message
     */
    showLoading(message) {
        if (this.loadingText) {
            this.loadingText.textContent = message;
        }
        this.loadingOverlay?.classList.remove('hidden');
    }

    /**
     * Update loading text
     * @param {string} message - Loading message
     */
    updateLoadingText(message) {
        if (this.loadingText) {
            this.loadingText.textContent = message;
        }
    }

    /**
     * Hide loading overlay
     */
    hideLoading() {
        this.loadingOverlay?.classList.add('hidden');
    }

    /**
     * Update status message
     * @param {string} message - Status message
     * @param {string} type - Message type (success, error, info)
     */
    updateStatus(message, type = 'success') {
        if (!this.statusText) return;
        
        this.statusText.textContent = message;
        this.statusText.className = `text-sm ${
            type === 'success' ? 'status-success' : 
            type === 'error' ? 'status-error' : 
            'status-info'
        }`;
        
        // Auto-hide after delay
        setTimeout(() => {
            if (this.statusText.textContent === message) {
                this.statusText.textContent = 'Ready';
                this.statusText.className = 'text-sm status-success';
            }
        }, STATUS_CONFIG.AUTO_HIDE_DELAY);
    }

    /**
     * Update FPS counter (call in animation loop)
     */
    updateFPS() {
        this.frameCount++;
        const currentTime = performance.now();
        
        if (currentTime >= this.lastTime + FPS_CONFIG.UPDATE_INTERVAL) {
            this.fps = Math.round((this.frameCount * 1000) / (currentTime - this.lastTime));
            this.frameCount = 0;
            this.lastTime = currentTime;
            
            if (this.fpsText) {
                this.fpsText.textContent = `FPS: ${this.fps}`;
            }
        }
    }

    /**
     * Update zoom indicator
     * @param {number} level - Current zoom level (1-based)
     * @param {number} maxLevel - Maximum zoom level
     */
    updateZoomIndicator(level, maxLevel) {
        if (this.zoomIndicator) {
            this.zoomIndicator.textContent = `🔍 Zoom: ${level}/${maxLevel}`;
        }
    }

    /**
     * Add image button to the list
     * @param {Object} imageData - Image data
     * @param {number} index - Image index
     * @param {boolean} isActive - Whether this is the current image
     * @param {Function} onClick - Click callback
     */
    addImageButton(imageData, index, isActive, onClick) {
        const button = document.createElement('button');
        button.className = `w-full px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
            isActive ? 'bg-blue-600 text-white' : 'bg-slate-700 hover:bg-slate-600 text-white'
        }`;
        // Use name from imageData or file.name as fallback
        const displayName = imageData.name || (imageData.file && imageData.file.name) || 'Unknown';
        button.textContent = `Image ${index + 1}: ${displayName}`;
        button.id = `image-btn-${index}`;
        button.dataset.imageIndex = index; // Store the image index
        
        button.addEventListener('click', onClick);
        
        this.imageList.appendChild(button);
    }

    /**
     * Update image button styles
     * @param {number} activeIndex - Index of active image
     */
    updateImageButtonStyles(activeIndex) {
        const buttons = this.imageList.querySelectorAll('button');
        buttons.forEach((button) => {
            const buttonImageIndex = parseInt(button.dataset.imageIndex);
            if (buttonImageIndex === activeIndex) {
                button.className = 'w-full px-3 py-2 text-sm rounded-lg transition-all duration-200 bg-blue-600 text-white';
            } else {
                button.className = 'w-full px-3 py-2 text-sm rounded-lg transition-all duration-200 bg-slate-700 hover:bg-slate-600 text-white';
            }
        });
    }

    /**
     * Open custom image modal
     */
    openCustomImageModal() {
        this.customImageModal?.classList.remove('hidden');
        this.customImagePreview?.classList.add('hidden');
        
        const placeBtn = document.getElementById('place-custom-image');
        if (placeBtn) placeBtn.disabled = true;
        
        // Reset size selection to medium
        const sizeButtons = document.querySelectorAll('.custom-size-btn');
        sizeButtons.forEach(btn => {
            btn.classList.remove('bg-blue-600', 'text-white');
            btn.classList.add('bg-slate-700', 'hover:bg-slate-600');
            if (btn.getAttribute('data-size') === 'medium') {
                btn.classList.remove('bg-slate-700', 'hover:bg-slate-600');
                btn.classList.add('bg-blue-600', 'text-white');
            }
        });
    }

    /**
     * Close custom image modal
     */
    closeCustomImageModal() {
        this.customImageModal?.classList.add('hidden');
    }

    /**
     * Show custom image preview
     * @param {string} url - Image URL
     */
    showCustomImagePreview(url) {
        if (this.previewImg) {
            this.previewImg.src = url;
        }
        this.customImagePreview?.classList.remove('hidden');
        
        const placeBtn = document.getElementById('place-custom-image');
        if (placeBtn) placeBtn.disabled = false;
    }

    /**
     * Open connection modal
     */
    openConnectionModal() {
        this.connectionModal?.classList.remove('hidden');
        this.updateConnectionStatus('Select the destination image');
    }

    /**
     * Close connection modal
     */
    closeConnectionModal() {
        this.connectionModal?.classList.add('hidden');
    }

    /**
     * Populate connection image select
     * @param {Array} images - Array of image data
     * @param {number} currentIndex - Current image index to exclude
     */
    populateConnectionImageSelect(images, currentIndex) {
        if (!this.connectionImageSelect) return;
        
        this.connectionImageSelect.innerHTML = '<option value="">Select an image...</option>';
        
        images.forEach((image, index) => {
            if (index !== currentIndex) {
                const option = document.createElement('option');
                option.value = index;
                option.textContent = image.name;
                this.connectionImageSelect.appendChild(option);
            }
        });
    }

    /**
     * Update connection status message
     * @param {string} message - Status message
     */
    updateConnectionStatus(message) {
        if (this.connectionStatus) {
            this.connectionStatus.textContent = message;
        }
    }

    /**
     * Enable/disable place connection button
     * @param {boolean} enabled - Whether to enable
     */
    setPlaceConnectionEnabled(enabled) {
        const placeBtn = document.getElementById('place-connection');
        if (placeBtn) {
            placeBtn.disabled = !enabled;
        }
    }

    /**
     * Get current initial rotation value
     * @returns {number|null}
     */
    getInitialRotation() {
        return this.currentInitialRotation;
    }
}

